import React, { useEffect, useState } from "react";
import {
  FirebaseActivity,
  Resource,
  Activity,
  TeacherReference,
  Submission,
  Klass,
} from "../types";
import firebase from "firebase";
import { useParams } from "react-router-dom";
import SubHeader from "../components/SubHeader";
import moment from "moment";
import Wrapper from "../components/Layout/Wrapper";
import styled from "styled-components";
import useAuth from "../hooks/useAuth";
import EditComponent from "../components/EditComponent";
import MarkdownIt from "markdown-it";
import MdEditor from "react-markdown-editor-lite";
import "react-markdown-editor-lite/lib/index.css";
import Button from "../components/Button";
import Teacher from "../components/Teacher";
import useTeachers from "../hooks/useTeachers";
import { Input, notification, Select, Switch, Upload } from "antd";
import { DatePicker } from "antd";
import { nanoid } from "nanoid";
import ResourceWrapper from "../components/ResourceWrapper";
import ActivitySubmissionRow from "../components/ActivitySubmissionRow";

const FormGroup = styled.div`
  margin-bottom: 8px;
  label {
    margin-right: 12px;
  }
`;

const { RangePicker } = DatePicker;

const mdParser = new MarkdownIt();

const ContentPageWrapper = styled.div`
  padding-bottom: 32px;
  .sub-header {
    margin-bottom: 16px;
  }
  main {
    width: 65%;
    margin-right: 5%;
  }
  aside {
    width: 30%;
  }
`;

const ActivityPage = () => {
  const { isAdmin, isSuperUser, accountDetails } = useAuth();
  const { activityId, classId } = useParams<{
    activityId: string;
    classId?: string;
  }>();
  const [newTeacher, setNewTeacher] = useState<TeacherReference>({
    teacherId: "",
    role: "Teacher",
  });
  const [submissions, setSubmissions] = useState<Submission[]>();
  const [activeActivity, setActiveActivity] = useState<Activity>();
  const [activeClass, setActiveClass] = useState<Klass>();
  const [submissionString, setSubmissionString] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const { data: teacherData } = useTeachers();

  useEffect(() => {
    setIsLoading(true);
    if (!classId) {
      firebase
        .firestore()
        .collection("activities")
        .doc(activityId)
        .get()
        .then((snapshot) => {
          const data = snapshot.data() as FirebaseActivity;
          if (!data) {
            return undefined;
          }
          return {
            ...data,
            id: snapshot.id,
            startTime: data?.startTime?.toMillis
              ? data.startTime.toMillis()
              : data.startTime,
            finishTime: data?.finishTime?.toMillis
              ? data.finishTime.toMillis()
              : data.finishTime,
          } as Activity;
        })
        .then(setActiveActivity)
        .then(() => setIsLoading(false));
    } else {
      firebase
        .firestore()
        .collection("classes")
        .doc(classId)
        .get()
        .then((snapshot) => {
          const data = snapshot.data() as Klass;
          setActiveClass(data);
          const activ = (data.activities || []).find(
            (act) => act.id === activityId
          );
          return activ;
        })
        .then(setActiveActivity)
        .then(() => setIsLoading(false));
    }
  }, [activityId, classId]);

  useEffect(() => {
    const getSubmissions = async () => {
      const submissions = firebase
        .firestore()
        .collection("submissions")
        .where("activityId", "==", activityId);

      // If an admin
      if (isAdmin) {
        const allSubmissions: Submission[] = [];
        (await submissions.get()).forEach((res) => {
          allSubmissions.push(res.data() as Submission);
        });
        return allSubmissions;
      }

      // Otherwise a student
      if (accountDetails?.id) {
        const studentSubmissions: Submission[] = [];
        (
          await submissions.where("userId", "==", accountDetails.id).get()
        ).forEach((res) => {
          studentSubmissions.push(res.data() as Submission);
        });
        if (studentSubmissions.length > 1) {
          setSubmissionString(studentSubmissions[0].string);
        }
        return studentSubmissions;
      }
    };

    if (activityId) {
      getSubmissions().then((next) => {
        setSubmissions(next);
      });
    }
  }, [activityId, isAdmin, accountDetails]);

  const onItemUpdate = async (nextActiv: Partial<Activity>) => {
    const nextActivity = { ...activeActivity, ...nextActiv } as Activity;
    if (activeClass) {
      const nextClass = { ...activeClass };
      nextClass.activities = (nextClass.activities || []).map((x) =>
        x.id === activityId ? nextActivity : x
      );
      await firebase
        .firestore()
        .collection("classes")
        .doc(classId)
        .update(nextClass);
      setActiveClass(nextClass);
      setActiveActivity(nextActivity);
      notification.success({ message: "Saved!" });
      return;
    }
    await firebase
      .firestore()
      .collection("activities")
      .doc(activityId)
      .update({
        ...nextActivity,
      })
      .then(() => {
        notification.success({ message: "Saved!" });
      });

    return nextActivity;
  };

  const onRemoveTeacher = (oldTeacherId?: string) => {
    const nextTeachersArray = (activeActivity?.teachers || []).filter(
      (x) => x.teacherId !== oldTeacherId
    );
    firebase.firestore().collection("activities").doc(activityId).update({
      teachers: nextTeachersArray,
    });
    notification.success({ message: "Saved!" });
    if (activeActivity) {
      setActiveActivity({
        ...activeActivity,
        teachers: nextTeachersArray,
      });
    }
  };

  const onAddTeacher = (nextTeacher?: TeacherReference) => {
    const nextTeachersArray = [
      ...(activeActivity?.teachers || []),
      nextTeacher,
    ] as TeacherReference[];
    firebase.firestore().collection("activities").doc(activityId).update({
      teachers: nextTeachersArray,
    });
    notification.success({ message: "Saved!" });
    if (activeActivity) {
      setActiveActivity({
        ...activeActivity,
        teachers: nextTeachersArray,
      });
    }
  };

  if (isLoading) {
    return <div>Loading</div>;
  }
  if (!activeActivity) {
    return <div>This Activity doesnt exist</div>;
  }
  const unassignedTeachers = (teacherData || []).filter(
    (x) =>
      !(activeActivity?.teachers || []).some((t) => t.teacherId === x.teacherId)
  );

  const renderSubmissions = () => {
    if (isAdmin && classId) {
      return (
        <div>
          <h3>All submissions</h3>
          {submissions?.length === 0 && "No submissions yet"}
          <table className="yc-table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Student</th>
                <th>Date</th>
                <th>Submission</th>
                <th>Award</th>
              </tr>
            </thead>
            <tbody>
              {submissions?.map((submission) => (
                <ActivitySubmissionRow
                  submission={submission}
                  defaultStars={activeActivity.stars}
                  onSetSubmissions={(sub) =>
                    setSubmissions(
                      [...submissions].map((x) => (x.id === sub.id ? sub : x))
                    )
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (!isAdmin) {
      return (
        <div>
          {submissions?.length !== 0 && <h3>Your submission</h3>}
          {submissions?.map((submission) => (
            <div>
              {submission.string && submission.string}
              {submission.upload?.path && (
                <ResourceWrapper
                  path={submission.upload?.path}
                  name={submission.upload?.name}
                  type={submission.upload?.type}
                />
              )}
            </div>
          ))}
          {(submissions?.filter((su) => su.marked) || []).length > 0 ? (
            <div style={{ marginTop: 32 }}>
              <strong>Your submission has been marked!</strong>
            </div>
          ) : (
            <>
              <h3>Submit your work</h3>
              {activeActivity.acceptString && (
                <>
                  <textarea
                    style={{ width: "100%" }}
                    value={submissionString}
                    onChange={(e) => setSubmissionString(e.currentTarget.value)}
                  ></textarea>
                  <br />
                  <Button
                    small
                    onClick={() => {
                      if (!submissionString) {
                        notification.error({ message: "No text entered" });
                        return;
                      }
                      // delete old submission if exists
                      if (submissions?.[0]) {
                        firebase
                          .firestore()
                          .collection("submissions")
                          .doc(submissions[0].id)
                          .delete();
                      }

                      const nextSubmission: Submission = {
                        id: nanoid(),
                        name: "string",
                        userId: accountDetails?.id || "",
                        userName:
                          accountDetails?.name ||
                          accountDetails?.username ||
                          "unknown",
                        activityId,
                        type: "STRING",
                        string: submissionString,
                        date: Date.now(),
                      };

                      firebase
                        .firestore()
                        .collection("submissions")
                        .doc(nextSubmission.id)
                        .set(nextSubmission);
                      notification.success({ message: "Submission sent!" });
                      setSubmissions([nextSubmission]);
                      setSubmissionString("");
                    }}
                  >
                    Submit
                  </Button>
                </>
              )}
              {activeActivity.acceptUpload && (
                <Upload
                  name="resource"
                  customRequest={async ({ file, onSuccess }) => {
                    if (submissions?.[0]) {
                      firebase
                        .firestore()
                        .collection("submissions")
                        .doc(submissions[0].id)
                        .delete();
                    }
                    const path = `Submissions/${activityId}/${accountDetails?.uid}/${file.name}`;
                    const arrayBuffer = await file.arrayBuffer();
                    const nextResource: Resource = {
                      name: file.name,
                      path,
                      type: file.type,
                    };
                    firebase.storage().ref(path).put(arrayBuffer);
                    const nextSubmission: Submission = {
                      id: nanoid(),
                      name: "upload",
                      userId: accountDetails?.id || "",
                      userName:
                        accountDetails?.name || accountDetails?.username || "",
                      activityId,
                      type: "STRING",
                      upload: nextResource,
                      date: Date.now(),
                    };
                    firebase
                      .firestore()
                      .collection("submissions")
                      .doc(nextSubmission.id)
                      .set(nextSubmission);

                    setSubmissions([nextSubmission]);
                    notification.success({ message: "Success" });
                    onSuccess({ msg: "ok" }, file);
                  }}
                  fileList={[]}
                >
                  <Button small>Upload submission</Button>
                </Upload>
              )}{" "}
            </>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <ContentPageWrapper>
      <SubHeader
        title={activeActivity?.name}
        subtitle={`Due: ${moment(activeActivity?.finishTime).format("LLLL")}`}
        buttonText="Join Activity"
        style={{ filter: `grayscale(${activeActivity.isVisible ? "0" : "1"})` }}
      />
      <Wrapper style={{ justifyContent: "space-between" }}>
        <main>
          {isAdmin && (
            <>
              <h3>Admin</h3>
              {classId && (
                <>
                  <FormGroup>
                    <label>Is Active?</label>
                    <Switch
                      checked={activeActivity.isVisible}
                      onChange={(next) => {
                        setActiveActivity({
                          ...activeActivity,
                          isVisible: next,
                        });
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Stars</label>
                    <Input
                      value={activeActivity.stars}
                      onChange={(e) =>
                        setActiveActivity({
                          ...activeActivity,
                          stars: Number(e.currentTarget.value),
                        })
                      }
                    />
                  </FormGroup>
                </>
              )}
              <FormGroup>
                <label>Name</label>
                <input
                  value={activeActivity.name}
                  onChange={(e) =>
                    setActiveActivity({
                      ...activeActivity,
                      name: e.currentTarget.value,
                    })
                  }
                />
              </FormGroup>

              <FormGroup>
                <label>
                  Submission type:{" "}
                  {activeActivity.acceptUpload ? "UPLOAD" : "STRING"}
                </label>
                <Switch
                  checked={activeActivity.acceptUpload}
                  onChange={(next) => {
                    setActiveActivity({
                      ...activeActivity,
                      acceptString: !next,
                      acceptUpload: next,
                    });
                  }}
                />
              </FormGroup>

              {classId && (
                <RangePicker
                  showTime={{
                    hideDisabledOptions: true,
                  }}
                  defaultValue={[
                    moment(activeActivity.startTime),
                    moment(activeActivity.finishTime),
                  ]}
                  onCalendarChange={(next) => {
                    if (next?.[0] && next?.[1]) {
                      setActiveActivity({
                        ...activeActivity,
                        startTime: +next[0],
                        finishTime: +next[1],
                      });
                    }
                  }}
                  format="YYYY-MM-DD HH:mm:ss"
                />
              )}
              <Button
                small
                onClick={() => {
                  onItemUpdate({ ...activeActivity });
                }}
              >
                Save
              </Button>
            </>
          )}

          <h3>Description</h3>
          <EditComponent
            enableEdit={isAdmin}
            editor={({ onFinish }) => {
              return (
                <>
                  <MdEditor
                    value={activeActivity.description}
                    style={{ height: "500px" }}
                    renderHTML={(text) => mdParser.render(text)}
                    onChange={(next) =>
                      setActiveActivity({
                        ...activeActivity,
                        description: next.text,
                      })
                    }
                  />
                  <Button
                    small
                    style={{ marginTop: 16 }}
                    onClick={() => {
                      onItemUpdate({ description: activeActivity.description });
                      onFinish(activeActivity.description);
                    }}
                  >
                    Update
                  </Button>
                </>
              );
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: mdParser.render(activeActivity.description),
              }}
            />
          </EditComponent>
          {(activeActivity?.resources || []).length > 0 && (
            <>
              <h3>Resources</h3>
              {activeActivity.resources.map((resource) => (
                <ResourceWrapper
                  path={resource.path}
                  name={resource.name}
                  type={resource.type}
                />
              ))}
            </>
          )}
          {isAdmin && (
            <div style={{ marginTop: 16 }}>
              <Upload
                name="resource"
                customRequest={async ({ file, onSuccess }) => {
                  const imagePath = `Activity/${activityId}/${file.name}`;
                  const arrayBuffer = await file.arrayBuffer();
                  const nextResources = [
                    ...(activeActivity?.resources || []),
                    {
                      name: file.name,
                      path: imagePath,
                      type: file.type,
                    },
                  ] as Resource[];
                  firebase.storage().ref(imagePath).put(arrayBuffer);
                  firebase
                    .firestore()
                    .collection("activities")
                    .doc(activityId)
                    .update({
                      resources: nextResources,
                    })
                    .then(() => {
                      setActiveActivity({
                        ...activeActivity,
                        resources: nextResources,
                      });
                    });

                  onSuccess({ msg: "ok" }, file);
                }}
                fileList={[]}
              >
                <Button small>Upload resources</Button>
              </Upload>
            </div>
          )}

          {renderSubmissions()}
        </main>
        <aside>
          <h3>Teachers</h3>
          {(activeClass?.teachers || []).map((teacher) => {
            const teacherDetail = teacherData?.find(
              (x) => x.teacherId === teacher.teacherId
            );
            return <Teacher teacher={teacherDetail} subtitle={teacher.role} />;
          })}
          {(activeActivity?.teachers || []).map((teacher) => {
            const teacherDetail = teacherData?.find(
              (x) => x.teacherId === teacher.teacherId
            );
            return (
              <Teacher
                teacher={teacherDetail}
                subtitle={teacher.role}
                onRemove={isAdmin ? onRemoveTeacher : undefined}
              />
            );
          })}
          {(activeActivity?.teachers || []).map((teacher) => {
            const teacherDetail = teacherData?.find(
              (x) => x.teacherId === teacher.teacherId
            );
            return (
              <Teacher
                teacher={teacherDetail}
                subtitle={teacher.role}
                onRemove={isAdmin ? onRemoveTeacher : undefined}
              />
            );
          })}
          {isSuperUser && unassignedTeachers.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <Select
                onChange={(next) =>
                  setNewTeacher({
                    role: newTeacher?.role,
                    teacherId: next as string,
                  })
                }
                value={newTeacher.teacherId}
              >
                <Select.Option value="">-- Select a teacher --</Select.Option>
                {unassignedTeachers.map((x) => (
                  <Select.Option value={x.teacherId}>{x.name}</Select.Option>
                ))}
              </Select>
              <br />
              <Select
                onChange={(next) =>
                  setNewTeacher({
                    teacherId: newTeacher?.teacherId,
                    role: next as string,
                  })
                }
                value={newTeacher.role}
              >
                <Select.Option value="">-- Select a role --</Select.Option>
                {["Teacher", "Assistant"].map((x) => (
                  <Select.Option value={x}>{x}</Select.Option>
                ))}
              </Select>
              <br />
              <Button small onClick={() => onAddTeacher(newTeacher)}>
                Add
              </Button>
            </div>
          )}
        </aside>
      </Wrapper>
    </ContentPageWrapper>
  );
};

export default ActivityPage;
